import { Inject, Injectable } from '@angular/core';
import { ENV, IEnvironment } from 'env/config';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Service } from '../entity/index';
import { DataService } from '../http/dataService';
import { CentralApis } from '../interfaces/internal';
import { CPMSUserInfo } from '../models/cpmsSalesRep.model';

export interface Organization {
  chnlOrgId: number;
  chnlOrgCd: string;
  chnlOrgTypeCd: string;
  chnlOrgLegalName: string;
  chnlOrgDes: string;
  adminRegionId: string;
  nccsDealerId: string;
  activeInd: boolean;
}

export interface Outlet {
  outletId: number;
  chnlOrgId: number;
  outletDes: string;
  outletCd: string;
  nccsDealerCode: string;
  activeInd: boolean;
}

export interface SalesRep {
  salesRepId: string;
  salesRepPin: string;
  firstName: string;
  lastName: string;
  chnlOrgId: string;
  salesRepCd: string;
  activeInd: boolean;
  preferredLanguageInd: string;
  nccsSalesRepId: string;
  associatedOutletIds: string[];
}

@Service({
  api: 'cpms',
  model: Object,
})
@Injectable({ providedIn: 'root' })
export class CPMSService {
  private baseUrl: string;

  constructor(
    @Inject(ENV) env: IEnvironment,
    private dataService: DataService
  ) {
    this.baseUrl = (env.api.backend.central.endpoint as CentralApis).iam;
  }

  /**
   * [DPD-2758] Get organizations from CPMS with server-side filtering.
   * - orgIds = undefined  → no restriction (admin/team roles): calls GET /cpms/organization → all orgs
   * - orgIds = ['id']     → restricted role with known orgId: calls GET /cpms/organization?ids=id → server-filtered
   * - orgIds = []         → restricted role but orgId missing: returns empty list (fail-closed, no API call)
   * @param orgIds Optional array of org IDs to restrict results to. Pass undefined for unrestricted access.
   * @returns Observable<Organization[]>
   */
  getOrganizations(orgIds?: number[]): Observable<Organization[]> {
    if (orgIds !== undefined && orgIds.length === 0) {
      return of([]);
    }

    if (orgIds?.length > 0) {
      const url = `${this.baseUrl}cpms/organization`;
      const rawQuery = `ids=[${orgIds.join(',')}]`;
      return this.dataService
        .get<Organization[]>(url, rawQuery as any, undefined, true)
        .pipe(map((response: any) => response as Organization[]));
    }

    return this.dataService
      .get<Organization[]>(`${this.baseUrl}cpms/organization`)
      .pipe(map((response: any) => response as Organization[]));
  }

  /**
   *Get outlets by organization ID with optional outlet filtering.
   * - outletIds = undefined → no restriction: calls GET /cpms/outlet?channelOrgId=X → all outlets
   * - outletIds = [x,y]    → restricted role (Team Lead): calls GET /cpms/outlet?channelOrgId=X&ids=[x,y]
   * - outletIds = []       → restricted role but no outlets assigned: returns empty list (fail-closed)
   * @param channelOrgId Organization ID (mandatory)
   * @param outletIds Optional array of outlet IDs to restrict results to (from user.cpmsOutletIds)
   * @returns Observable<Outlet[]>
   */
  getOutletsByOrgId(
    channelOrgId: string,
    outletIds?: number[]
  ): Observable<Outlet[]> {
    if (outletIds !== undefined && outletIds.length === 0) {
      return of([]);
    }

    if (outletIds?.length > 0) {
      const url = `${this.baseUrl}cpms/outlet`;
      const rawQuery = `chnlOrgId=${channelOrgId}&ids=[${outletIds.join(',')}]`;
      return this.dataService
        .get<Outlet[]>(url, rawQuery as any, undefined, true)
        .pipe(map((response: any) => response as Outlet[]));
    }

    const url = `${this.baseUrl}cpms/outlet?chnlOrgId=${channelOrgId}`;
    return this.dataService
      .get(url)
      .pipe(map((response: any) => response as Outlet[]));
  }

  /**
   * Get sales reps by org ID and outlet ID
   * @param channelOrgId Org ID
   * @param outletId Outlet ID
   * @returns Observable<SalesRep[]>
   */
  getSalesRepsByOutletId(
    channelOrgId: string,
    outletId: string
  ): Observable<SalesRep[]> {
    const url = `${this.baseUrl}cpms/salesRep?outletId=${outletId}&chnlOrgId=${channelOrgId}`;
    return this.dataService.get(url).pipe(
      map((response: any) => {
        return response as SalesRep[];
      })
    );
  }

  /**
   * Get CPMS user context by username
   * Returns user's organization, outlets, and sales rep information
   * Used for authorization filtering - vendor managers can only see their assigned outlets
   * @param username User's login username (from token sub field)
   * @returns Observable<CPMSUserInfo>
   */
  getCPMSUserInfo(username: string): Observable<CPMSUserInfo> {
    const url = `${this.baseUrl}cpms/username/${username}`;
    return this.dataService.get(url).pipe(
      map((response: any) => {
        return response as CPMSUserInfo;
      })
    );
  }
}
